import { FormControl, List, RadioGroup } from "@mui/material";
import { useStore } from "csi/state/hooks";
import rootStore from "csi/state/rootStore";
import { cnl } from "csi/typings";
import { Box, ConfirmDialog, FormControlLabel, GridColumn, Switch } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, FunctionComponent } from "react";
import { EmptyEnvironments } from "./EmptyEnvironments";
import { EnvironmentItem } from "./EnvironmentItem";
import { EnvironmentSlider } from "./EnvironmentSlider";

const LIST_STYLES = {
	height: "100%",
	overflow: "auto",
	maxHeight: 400,
	border: STANDARD_BORDER_STYLE,
	borderColor: grey[200],
	"& .MuiFormControl-root": {
		width: "100%",
	},
};

const SWITCH_STYLES = {
	justifyContent: "space-between",
	padding: "10px 0px",
};

const EnvironmentsList: FunctionComponent = observer(() => {
	const loc = useLocale();
	const { rightPaneStore } = useStore();
	const {
		handleEnvironmentSelectChange,
		updateEnvironmentExposure,
		updateEnvironmentOrientation,
		updateEnvironmentBackground,
		selectedEnvironment,
		environmentCanReset,
		environments,
		environmentExposure,
		environmentBackground,
		environmentOrientation,
	} = rootStore.threeDStore.environmentsStore;

	const confirmEnvironmentChange = (event: React.ChangeEvent<HTMLInputElement>, environmentURL: cnl) => {
		rightPaneStore.openDialog(ConfirmDialog, {
			category: "error",
			confirmRes: "BUTTON_CONTINUE_ANYWAY",
			messageRes: "3DUnsavedEnvironmentChanges",
			messageResParams: [loc.getNodeName(selectedEnvironment)],
			onConfirm: () => {
				handleEnvironmentSelectChange(event, environmentURL);
			},
			showClose: true,
			titleRes: "UnsavedChanges",
			zIndex: 1000,
		});
	};

	return (
		<GridColumn
			scroll
			spacing={3}
			spacingAround={3}
		>
			<List sx={LIST_STYLES}>
				<FormControl>
					<RadioGroup
						name="environment-buttons-group"
						onChange={environmentCanReset ? confirmEnvironmentChange : handleEnvironmentSelectChange}
						value={selectedEnvironment?.$URL || null}
					>
						{environments.map((item) => (
							<EnvironmentItem
								checked={selectedEnvironment?.$URL === item.$URL}
								item={item}
								key={item.$URL}
							/>
						),
						)}
					</RadioGroup>
				</FormControl>
			</List>
			{selectedEnvironment && (
				<>
					<EnvironmentSlider
						degrees
						item="Orientation"
						max={360}
						onValueChange={updateEnvironmentOrientation}
						value={environmentOrientation}
					/>
					<EnvironmentSlider
						item="Exposure"
						max={100}
						onValueChange={updateEnvironmentExposure}
						value={environmentExposure}
					/>
					<FormControl fullWidth>
						<FormControlLabel
							block
							control={(
								<Switch
									checked={environmentBackground}
									onChange={(event: ChangeEvent<HTMLInputElement>, checked: boolean) => { updateEnvironmentBackground(checked); }}
								/>
							)}
							labelPlacement="start"
							res="Background"
							sx={SWITCH_STYLES}
						/>
					</FormControl>
				</>
			)}
		</GridColumn>
	);
});

export const EnviornmentLighting: FunctionComponent = observer(() => {
	const { hasEnvironments } = rootStore.threeDStore.environmentsStore;

	return (
		<Box height="100%">
			{!hasEnvironments &&
                <EmptyEnvironments />
			}
			{hasEnvironments &&
                <EnvironmentsList />
			}
		</Box>
	);
});